/**
 * search-page.js — v3, redesigned per Figma spec
 * https://www.figma.com/design/iK64Z88QhoWHZto42lRHkK/Artline-Website?node-id=2944-6242
 *
 * Still bypasses the broken `search.results` — see v1 header comment for why.
 * Product rows now also show price + volume-variant pills, which requires
 * one extra lightweight fetch per product (/products/{handle}.js, public,
 * no auth) since predictive_search's product resource doesn't include
 * variants. Done in parallel, only for the up-to-10 products on screen.
 */
(function () {
  'use strict';

  function qs(name) {
    var params = new URLSearchParams(window.location.search);
    return params.get(name);
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str || '';
    return div.innerHTML;
  }

  // Wraps every case-insensitive occurrence of any word from `query`
  // inside `text` in <mark>, escaping everything else. Used to echo the
  // highlight style Shopify already applies natively to query suggestions,
  // extended to product/collection/page/article titles which don't get it
  // for free, and matched per-word so "extra honey resin" still highlights
  // "Extra Honey" and "Resin" separately inside a differently-ordered title.
  function highlightMatches(text, query) {
    text = text || '';
    if (!query) return escapeHtml(text);
    var words = query.trim().split(/\s+/).filter(Boolean)
      .map(function (w) { return w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); });
    if (!words.length) return escapeHtml(text);
    var re = new RegExp('(' + words.join('|') + ')', 'ig');
    var parts = text.split(re);
    return parts.map(function (part, i) {
      var esc = escapeHtml(part);
      return (i % 2 === 1) ? '<mark>' + esc + '</mark>' : esc;
    }).join('');
  }

  function formatPrice(cents) {
    if (window.theme && theme.Currency && typeof theme.Currency.formatMoney === 'function') {
      return theme.Currency.formatMoney(cents, theme.moneyFormat);
    }
    return (cents / 100).toFixed(2);
  }

  // ---- data fetching -------------------------------------------------

  function fetchProductVariants(products) {
    return Promise.all(
      products.map(function (p) {
        var baseUrl = p.url.split('?')[0];
        var handle = baseUrl.split('/products/')[1];
        return fetch('/products/' + handle + '.js')
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (full) {
            if (full && isColorOption(full)) {
              return fetchColorSwatchStyles(baseUrl).then(function (styleMap) {
                return { search: p, full: full, swatchStyles: styleMap };
              });
            }
            return { search: p, full: full, swatchStyles: {} };
          })
          .catch(function () { return { search: p, full: null, swatchStyles: {} }; });
      })
    );
  }

  function filterNonEmptyCollections(collections) {
    return Promise.all(
      collections.map(function (c) {
        var handle = c.url.split('/collections/')[1].split('?')[0];
        return fetch('/collections/' + handle + '/products.json?limit=1')
          .then(function (r) { return r.ok ? r.json() : { products: [] }; })
          .then(function (data) { return (data.products && data.products.length > 0) ? c : null; })
          .catch(function () { return c; });
      })
    ).then(function (results) { return results.filter(Boolean); });
  }

  function filterNonEmptyPages(pages) {
    return Promise.all(
      pages.map(function (p) {
        var handle = p.url.split('/pages/')[1].split('?')[0];
        return fetch('/pages/' + handle)
          .then(function (r) { return r.ok ? r.text() : ''; })
          .then(function (html) {
            var mainMatch = html.match(/<main[^>]*id="MainContent"[^>]*>([\s\S]*?)<\/main>/);
            var inner = mainMatch ? mainMatch[1] : html;
            inner = inner.replace(/<script[\s\S]*?<\/script>/gi, '').replace(/<style[\s\S]*?<\/style>/gi, '');
            var hasWidget = /<(form|input|select|button|iframe|canvas|video)\b/i.test(inner);
            var text = inner.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
            if (p.title) {
              var titleRe = new RegExp(p.title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i');
              text = text.replace(titleRe, '').trim();
            }
            return (hasWidget || text.length > 0) ? p : null;
          })
          .catch(function () { return p; });
      })
    ).then(function (results) { return results.filter(Boolean); });
  }

  // ---- rendering -------------------------------------------------------

  // Mirrors product-grid-item.liquid: an option counts as "color" if its
  // name contains the theme's configured trigger word (default "color",
  // also catching "colour"). Everything else renders as pill buttons.
  function isColorOption(full) {
    var settings = window.searchPageSwatchSettings;
    if (!settings || !settings.enabled) return false;
    var optionName = (full.options && full.options[0] && full.options[0].name || '').toLowerCase();
    if (!optionName) return false;
    if (optionName.indexOf(settings.trigger) !== -1) return true;
    if (settings.trigger === 'color' && optionName.indexOf('colour') !== -1) return true;
    return false;
  }

  // Guessing the swatch color client-side (config map, or "last word as a
  // CSS keyword") misses cases where the real swatch is an uploaded image
  // (e.g. "Rainbow"/"Pastel" set variants use a custom PNG, not a flat
  // color — `background-color: rainbow` is not valid CSS and renders
  // blank). The product page itself already has this fully resolved by
  // Liquid (native swatch metafield → theme color_swatch_config → uploaded
  // file), so for color-option products we fetch that page once and lift
  // the real style string per variant instead of re-deriving it.
  function fetchColorSwatchStyles(baseUrl) {
    return fetch(baseUrl)
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (html) {
        var names = [];
        var nameRe = /data-color-name="([^"]+)"/g;
        var m;
        while ((m = nameRe.exec(html))) names.push(m[1]);

        var styles = [];
        var styleRe = /class="variant__button-label color-swatch[^"]*"\s*style="([\s\S]*?)"/g;
        while ((m = styleRe.exec(html))) styles.push(m[1].trim());

        var map = {};
        names.forEach(function (name, i) {
          if (styles[i]) map[name] = styles[i];
        });
        return map;
      })
      .catch(function () { return {}; });
  }

  function colorSwatchesHtml(full, baseUrl, styleMap) {
    var html = '<div class="search-result-row__swatches">';
    full.variants.forEach(function (v, i) {
      var label = v.public_title || v.title;
      var style = styleMap[label] || '';
      html += '<a href="' + baseUrl + '?variant=' + v.id + '" class="search-swatch' + (i === 0 ? ' is-selected' : '') +
        '" style="' + style + '" title="' + escapeHtml(label) + '"><span class="visually-hidden">' + escapeHtml(label) + '</span></a>';
    });
    html += '</div>';
    return html;
  }

  function volumePillsHtml(full, baseUrl, swatchStyles) {
    if (!full || !full.variants || full.variants.length < 2) return '';
    if (isColorOption(full)) return colorSwatchesHtml(full, baseUrl, swatchStyles || {});
    var html = '<div class="search-result-row__pills">';
    full.variants.forEach(function (v, i) {
      html += '<a href="' + baseUrl + '?variant=' + v.id + '" class="search-pill' + (i === 0 ? ' is-selected' : '') + '">' + escapeHtml(v.public_title || v.title) + '</a>';
    });
    html += '</div>';
    return html;
  }

  function priceRowHtml(p, full) {
    var price = full ? full.price : Math.round(parseFloat(p.price_min) * 100);
    var compareAt = full ? full.compare_at_price : Math.round(parseFloat(p.compare_at_price_min) * 100);
    var onSale = compareAt && compareAt > price;

    var html = '<div class="search-result-row__price-row">';
    html += '<span class="search-result-row__price' + (onSale ? ' is-sale' : '') + '">' + formatPrice(price) + '</span>';
    if (onSale) {
      html += '<span class="search-result-row__price-compare">' + formatPrice(compareAt) + '</span>';
    }
    html += '</div>';
    return html;
  }

  function productRowHtml(entry, query) {
    var p = entry.search;
    var full = entry.full;
    var baseUrl = p.url.split('?')[0];
    var img = p.featured_image ? p.featured_image.url : (p.image || '');
    var imgTag = img
      ? '<img src="' + img.replace(/\.(jpg|jpeg|png|webp)(\?|$)/i, '_240x.$1$2') + '" alt="' + escapeHtml(p.title) + '" loading="lazy" width="120" height="120">'
      : '';

    // Image and title are separate links (rather than one <a> around the
    // whole row) specifically so the pills below can also be individual
    // links — nesting <a> inside <a> is invalid HTML and gets silently
    // broken apart by the browser.
    return (
      '<li class="search-result-row">' +
        '<a href="' + baseUrl + '" class="search-result-row__image">' + imgTag + '</a>' +
        '<div class="search-result-row__info">' +
          '<a href="' + baseUrl + '" class="search-result-row__title-link">' +
            '<h3 class="search-result-row__title">' + highlightMatches(p.title, query) + '</h3>' +
          '</a>' +
          priceRowHtml(p, full) +
          volumePillsHtml(full, baseUrl, entry.swatchStyles) +
        '</div>' +
      '</li>'
    );
  }

  function simpleLinkItem(item, query) {
    return '<li><a href="' + item.url + '"><span>' + highlightMatches(item.title, query) + '</span></a></li>';
  }

  function suggestionItem(item) {
    // Shopify already returns styled_text with the matched prefix wrapped
    // in <mark> — reuse it directly instead of re-deriving from `text`.
    return '<li><a href="' + item.url + '"><span>' + item.styled_text + '</span></a></li>';
  }

  function cardItem(item, query) {
    var img = item.image || (item.featured_image ? item.featured_image.url : '');
    var imgTag = img
      ? '<img src="' + img.replace(/\.(jpg|jpeg|png|webp)(\?|$)/i, '_400x.$1$2') + '" alt="' + escapeHtml(item.title) + '" loading="lazy">'
      : '';
    return (
      '<li class="search-card">' +
        '<a href="' + item.url + '">' +
          '<div class="search-card__image">' + imgTag + '</div>' +
          '<span class="search-card__title">' + highlightMatches(item.title, query) + '</span>' +
        '</a>' +
      '</li>'
    );
  }

  var MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  function formatArticleDate(iso) {
    if (!iso) return '';
    var d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return MONTH_NAMES[d.getMonth()] + ' ' + d.getDate() + ', ' + d.getFullYear();
  }

  // Shopify's article resource doesn't include a ready-made excerpt
  // (summary_html is usually blank) or a reading time, unlike the Figma
  // spec's "date · N min read" meta line — both are derived here from the
  // full article body, which is included.
  function articleExcerptAndReadTime(bodyHtml) {
    var text = (bodyHtml || '')
      .replace(/<style[\s\S]*?<\/style>/gi, '')
      .replace(/<script[\s\S]*?<\/script>/gi, '')
      .replace(/<[^>]+>/g, ' ')
      .replace(/&[a-z]+;|&#\d+;/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    var excerpt = text.length > 150 ? text.slice(0, 150).replace(/\s+\S*$/, '') + '...' : text;
    var wordCount = text.length ? text.split(' ').length : 0;
    var minutes = Math.max(1, Math.round(wordCount / 200));

    return { excerpt: excerpt, minutes: minutes };
  }

  function articleRowHtml(item, query) {
    var img = item.image || (item.featured_image ? item.featured_image.url : '');
    var imgTag = img
      ? '<img src="' + img.replace(/\.(jpg|jpeg|png|webp)(\?|$)/i, '_240x.$1$2') + '" alt="' + escapeHtml(item.title) + '" loading="lazy">'
      : '';
    var meta = articleExcerptAndReadTime(item.body);
    var dateStr = formatArticleDate(item.published_at);

    var metaLine = '';
    if (dateStr) {
      metaLine = '<span class="search-article-row__date">' + escapeHtml(dateStr) + '</span>' +
        '<span class="search-article-row__dot">·</span>' +
        '<span class="search-article-row__readtime">' + meta.minutes + ' min read</span>';
    } else {
      metaLine = '<span class="search-article-row__readtime">' + meta.minutes + ' min read</span>';
    }

    return (
      '<li class="search-article-row">' +
        '<a href="' + item.url + '" class="search-article-row__image">' + imgTag + '</a>' +
        '<div class="search-article-row__info">' +
          '<a href="' + item.url + '" class="search-article-row__title-link">' +
            '<h3 class="search-article-row__title">' + highlightMatches(item.title, query) + '</h3>' +
          '</a>' +
          '<p class="search-article-row__excerpt">' + escapeHtml(meta.excerpt) + '</p>' +
          '<div class="search-article-row__meta">' + metaLine + '</div>' +
        '</div>' +
      '</li>'
    );
  }

  function renderEmpty(container, query) {
    container.innerHTML =
      '<div class="search-page__empty">' +
        '<p>No results found for “' + escapeHtml(query) + '”.</p>' +
        '<p><a href="/collections/shop-all">Browse all products →</a></p>' +
      '</div>';
  }

  var TAB_CONFIG = [
    { key: 'products', label: 'Products' },
    { key: 'suggestions', label: 'Suggestions' },
    { key: 'collections', label: 'Collections' },
    { key: 'pages', label: 'Pages' },
    { key: 'articles', label: 'Blog' }
  ];

  function panelContent(key, items, productEntries, query) {
    if (key === 'products') {
      return '<ul class="search-result-list">' + productEntries.map(function (e) { return productRowHtml(e, query); }).join('') + '</ul>';
    }
    if (key === 'articles') {
      return '<ul class="search-article-list">' + items.map(function (item) { return articleRowHtml(item, query); }).join('') + '</ul>';
    }
    if (key === 'collections') {
      return '<ul class="search-card-grid">' + items.map(function (item) { return cardItem(item, query); }).join('') + '</ul>';
    }
    if (key === 'suggestions') {
      return '<ul class="search-link-list">' + items.map(suggestionItem).join('') + '</ul>';
    }
    // pages
    return '<ul class="search-link-list">' + items.map(function (item) { return simpleLinkItem(item, query); }).join('') + '</ul>';
  }

  function render(container, queries, productEntries, collections, pages, articles, query) {
    var data = {
      products: productEntries,
      suggestions: queries,
      collections: collections,
      pages: pages,
      articles: articles
    };
    var tabs = TAB_CONFIG.filter(function (t) { return data[t.key].length > 0; });

    if (tabs.length === 0) {
      renderEmpty(container, query);
      return;
    }

    var tabsHtml = '<div class="search-page__tabs" role="tablist">';
    tabs.forEach(function (t, i) {
      tabsHtml +=
        '<button type="button" class="search-page__tab' + (i === 0 ? ' is-active' : '') + '"' +
          ' role="tab" data-tab-target="' + t.key + '"' +
          ' aria-selected="' + (i === 0 ? 'true' : 'false') + '">' +
          t.label + ' <span class="search-page__tab-count">(' + data[t.key].length + ')</span>' +
        '</button>';
    });
    tabsHtml += '</div>';

    var panelsHtml = '<div class="search-page__panels">';
    tabs.forEach(function (t, i) {
      panelsHtml +=
        '<div class="search-page__panel' + (i === 0 ? ' is-active' : '') + '"' +
          ' role="tabpanel" data-tab-panel="' + t.key + '">' +
          panelContent(t.key, data[t.key], productEntries, query) +
        '</div>';
    });
    panelsHtml += '</div>';

    container.innerHTML = tabsHtml + panelsHtml;

    container.querySelectorAll('[data-tab-target]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var target = btn.getAttribute('data-tab-target');
        container.querySelectorAll('[data-tab-target]').forEach(function (b) {
          b.classList.remove('is-active');
          b.setAttribute('aria-selected', 'false');
        });
        btn.classList.add('is-active');
        btn.setAttribute('aria-selected', 'true');
        container.querySelectorAll('[data-tab-panel]').forEach(function (panel) {
          panel.classList.toggle('is-active', panel.getAttribute('data-tab-panel') === target);
        });
      });
    });
  }

  // ---- init --------------------------------------------------------

  var searchToken = 0;

  function fetchAndRender(container, query, attempt, token) {
    var params = new URLSearchParams({
      q: query,
      'resources[type]': 'product,collection,page,article,query',
      'resources[limit]': 10,
      'resources[limit_scope]': 'each',
      'resources[options][unavailable_products]': 'last'
    });

    return fetch('/search/suggest.json?' + params.toString(), {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Search request failed: ' + res.status);
        return res.json();
      })
      .then(function (data) {
        var results = (data && data.resources && data.resources.results) || {};
        var queries = results.queries || [];

        // Shopify's predictive search matches against full description
        // body text, not just the title — e.g. a pigment paste whose
        // description happens to mention "resins" shows up for a "resin"
        // query even though the product itself isn't a resin. Restrict
        // to items whose own title contains at least one word of the
        // query (so "red pigment paste" still finds "Pigment Paste" even
        // though "red" isn't in that title), while still keeping the
        // match scoped to the title only — never the body — so it stays
        // relevant rather than reintroducing the description-noise
        // problem this filter exists to fix.
        var needleWords = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
        function titleMatches(item) {
          var title = (item.title || '').toLowerCase();
          return needleWords.some(function (w) { return title.indexOf(w) !== -1; });
        }

        var products = (results.products || []).filter(titleMatches);
        var articles = (results.articles || []).filter(titleMatches);
        var collectionsRaw = (results.collections || []).filter(titleMatches);
        var pagesRaw = (results.pages || []).filter(titleMatches);

        var totalRaw = (results.products || []).length + (results.articles || []).length +
          queries.length + (results.collections || []).length + (results.pages || []).length;

        // Shopify's predictive search endpoint occasionally returns a
        // transient empty payload for a valid query (backend hiccup, not
        // a real "no results"). Retry once, silently, before giving up.
        // Uses the unfiltered raw count so a genuinely title-filtered-to-
        // zero result doesn't trigger a pointless retry.
        if (totalRaw === 0 && attempt === 0) {
          return new Promise(function (resolve) { setTimeout(resolve, 500); })
            .then(function () { return fetchAndRender(container, query, attempt + 1, token); });
        }

        return Promise.all([
          fetchProductVariants(products),
          filterNonEmptyCollections(collectionsRaw),
          filterNonEmptyPages(pagesRaw)
        ]).then(function (out) {
          // A newer search started (person kept typing) while this one was
          // still in flight — its result is stale, drop it so it can't
          // clobber whatever the latest keystroke already rendered.
          if (token !== searchToken) return;
          render(container, queries, out[0], out[1], out[2], articles, query);
        });
      });
  }

  function runSearch(container, query) {
    var myToken = ++searchToken;
    container.setAttribute('aria-busy', 'true');
    container.innerHTML = '<div class="search-page__loading">Searching…</div>';

    return fetchAndRender(container, query, 0, myToken)
      .catch(function (err) {
        if (myToken !== searchToken) return;
        console.error('[search-page] error', err);
        container.innerHTML = '<p class="search-page__error">Search is temporarily unavailable. Please try again.</p>';
      })
      .finally(function () {
        if (myToken !== searchToken) return;
        container.removeAttribute('aria-busy');
      });
  }

  function init() {
    var container = document.querySelector('[data-search-page-results]');
    if (!container) return;

    var form = document.querySelector('[data-search-page-form]');
    var input = document.querySelector('[data-search-page-input]');

    // Re-run the search in place instead of a full page reload when the
    // person edits the query and hits enter — falls back to the form's
    // native GET submit (full reload, still correct) if this never runs.
    if (form && input) {
      form.addEventListener('submit', function (e) {
        var newQuery = input.value.trim();
        if (!newQuery) return; // let native submit handle empty (goes to plain /search)
        e.preventDefault();
        clearTimeout(debounceTimer);
        var newUrl = window.location.pathname + '?q=' + encodeURIComponent(newQuery);
        window.history.pushState({ q: newQuery }, '', newUrl);
        runSearch(container, newQuery);
      });
    }

    // Search as you type — Enter above still fires instantly; this covers
    // the case where someone just stops typing without pressing it.
    var debounceTimer = null;
    if (input) {
      input.addEventListener('input', function () {
        clearTimeout(debounceTimer);
        var val = input.value.trim();
        debounceTimer = setTimeout(function () {
          if (!val) {
            window.history.pushState({}, '', window.location.pathname);
            searchToken++; // invalidate any in-flight request
            container.innerHTML = '';
            return;
          }
          var newUrl = window.location.pathname + '?q=' + encodeURIComponent(val);
          window.history.pushState({ q: val }, '', newUrl);
          runSearch(container, val);
        }, 350);
      });
    }

    window.addEventListener('popstate', function () {
      var q = qs('q');
      if (input) input.value = q || '';
      if (q) {
        runSearch(container, q);
      } else {
        container.innerHTML = '';
      }
    });

    var query = qs('q');
    if (!query) {
      container.innerHTML = '';
      return;
    }

    var clearLink = document.querySelector('[data-search-page-clear]');
    if (clearLink) clearLink.href = '/search';

    runSearch(container, query);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
